/**
 * A station that prints.
 *
 * Plenty of kitchens want paper: a rail of tickets above the grill, spiked
 * when the plate goes. So a station can be a printer instead of a screen, and
 * this is the page that drives it — a browser left open on whatever sits next
 * to the printer, printing each ticket routed to its station as it arrives.
 *
 * WHY THE BROWSER PRINTS, AND WHAT THAT COSTS
 *
 * No extra software to install, and it works with any printer the machine
 * already knows. The cost is the print dialog: an ordinary browser asks before
 * every page, which is useless in a kitchen. Chrome started with
 * `--kiosk-printing` prints straight to the default printer without asking,
 * and that is how this is meant to run. The page says so, because the failure
 * looks like "printing does nothing" rather than like a missing flag.
 *
 * WHY IT HAS TO BE SWITCHED ON
 *
 * A laptop that opens this station by accident should not start printing the
 * evening's tickets. Switching on is remembered on the device, so the machine
 * by the printer keeps printing after a power cut or a reload, and nothing
 * else ever starts.
 *
 * WHY A PRINTED TICKET IS STAMPED PER DISH
 *
 * A side added to a course after its ticket printed joins the same ticket. The
 * server keeps a stamp per dish, the queue returns only what has not printed,
 * and a slip that follows an earlier one says so at the top — as does a void,
 * because a dish taken off a paper ticket is otherwise still cooked.
 */

import { useEffect, useRef, useState } from 'react';

import {
  displayLocale,
  useMarkPrinted,
  usePrintQueue,
  type PrintJob,
  type Station,
} from '@restaurant/api-client';
import { ActionError, Button, ErrorNotice, Loading } from '@restaurant/ui';

const armedKey = (stationId: string) => `kds.print.on.${stationId}`;
const HISTORY = 12;

function readArmed(stationId: string): boolean {
  try {
    return localStorage.getItem(armedKey(stationId)) === '1';
  } catch {
    return false;
  }
}

function writeArmed(stationId: string, on: boolean): void {
  try {
    if (on) localStorage.setItem(armedKey(stationId), '1');
    else localStorage.removeItem(armedKey(stationId));
  } catch {
    // Storage refused: printing still works until the page reloads.
  }
}

type Printed = { job: PrintJob; at: number };

export function PrinterView({ station }: { station: Station }) {
  const queue = usePrintQueue(station.id);
  const markPrinted = useMarkPrinted();
  const [armed, setArmed] = useState(() => readArmed(station.id));
  // What is on the slip right now. Rendered first, printed after the render
  // has reached the page — printing the previous slip is the bug to avoid.
  const [slip, setSlip] = useState<{ job: PrintJob; again: boolean } | null>(null);
  const [history, setHistory] = useState<Printed[]>([]);
  const busy = useRef(false);

  useEffect(() => setArmed(readArmed(station.id)), [station.id]);

  const waiting = queue.data ?? [];
  const next = waiting[0];

  // Take the oldest waiting ticket onto the slip.
  useEffect(() => {
    if (!armed || busy.current || slip || !next) return;
    // Printed already on this page and waiting on the server to agree: do
    // not print it again because a refetch landed before the stamp did.
    if (history.some((h) => h.job.ticket_id === next.ticket_id && sameItems(h.job, next)))
      return;
    busy.current = true;
    setSlip({ job: next, again: false });
  }, [armed, next, slip, history]);

  // The slip is on the page: print it, then say so.
  useEffect(() => {
    if (!slip) return;
    const timer = window.setTimeout(() => {
      // Blocks until the page has gone to the printer — or, without kiosk
      // printing, until somebody closes the dialog.
      window.print();
      const done = slip;
      setSlip(null);
      busy.current = false;
      setHistory((previous) =>
        [{ job: done.job, at: Date.now() }, ...previous].slice(0, HISTORY),
      );
      if (!done.again) {
        markPrinted.mutate({
          ticketId: done.job.ticket_id,
          itemIds: done.job.items.map((item) => item.id),
        });
      }
    }, 50);
    return () => window.clearTimeout(timer);
    // `markPrinted.mutate` is stable; the slip is the only trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slip]);

  function toggle() {
    const on = !armed;
    writeArmed(station.id, on);
    setArmed(on);
  }

  return (
    <div className="pr">
      <section className={`pr__state${armed ? ' is-on' : ''}`}>
        <p className="pr__headline">{armed ? 'Printing tickets' : 'Printing is off'}</p>
        <p className="pr__sub">
          {armed
            ? waiting.length === 0
              ? 'Tickets print here the moment a course is sent.'
              : `${waiting.length} waiting to print.`
            : 'Turn it on at the machine next to the printer. Only that one should print.'}
        </p>
        <Button variant={armed ? 'secondary' : 'primary'} size="lg" onClick={toggle}>
          {armed ? 'Pause printing' : 'Start printing here'}
        </Button>
        <p className="pr__hint">
          Chrome asks before every ticket unless it was started with{' '}
          <code>--kiosk-printing</code>, which prints straight to the default printer. Start it
          that way on this machine.
        </p>
      </section>

      {queue.isPending && <Loading label="Checking for tickets" />}
      {queue.isError && <ErrorNotice message={queue.error.message} />}
      <ActionError error={markPrinted.error} action="Recording a printed ticket" />

      <section className="pr__log" aria-label="Printed on this machine">
        <h2 className="pr__log-title">Printed here</h2>
        {history.length === 0 ? (
          <p className="pr__sub">Nothing yet.</p>
        ) : (
          <ul className="pr__list">
            {history.map(({ job, at }) => (
              <li key={`${job.ticket_id}-${at}`} className="pr__row">
                <span className="pr__row-where">
                  {job.table_label ? `Table ${job.table_label}` : `Order ${job.order_number}`}
                  {job.course_no > 1 && ` · Course ${job.course_no}`}
                  {job.from_location_name && ` · for ${job.from_location_name}`}
                </span>
                <span className="pr__row-what">
                  {job.follow_up ? 'Addition · ' : ''}
                  {job.items.length} dish{job.items.length === 1 ? '' : 'es'}
                </span>
                <span className="pr__row-at">{clock(at)}</span>
                {/* A ticket jammed, fell in the fryer, got spiked too early. */}
                <Button
                  size="sm"
                  disabled={slip !== null}
                  onClick={() => {
                    busy.current = true;
                    setSlip({ job, again: true });
                  }}
                >
                  Print again
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {slip && <Slip job={slip.job} again={slip.again} />}
    </div>
  );
}

/**
 * What goes on the paper. Hidden on screen, and the only thing that prints.
 *
 * Big and plain: read at arm's length on a rail through steam, on thermal
 * paper that fades. No colour, no rules thinner than a character.
 */
function Slip({ job, again }: { job: PrintJob; again: boolean }) {
  const voids = job.items.filter((item) => item.voided);
  const dishes = job.items.filter((item) => !item.voided);
  return (
    <div className="slip" aria-hidden="true">
      {again && <p className="slip__banner">REPRINT</p>}
      {job.follow_up && (
        <p className="slip__banner">{dishes.length === 0 ? 'VOID' : 'ADDED TO TICKET'}</p>
      )}
      {/* Cooked here for another site: the slip goes with the food. */}
      {job.from_location_name && (
        <p className="slip__banner">FOR {job.from_location_name.toUpperCase()}</p>
      )}
      <p className="slip__where">
        {job.table_label ?? `#${job.order_number}`}
        {job.table_label && <span className="slip__num"> #{job.order_number}</span>}
      </p>
      <p className="slip__meta">
        {job.course_no > 1 ? `Course ${job.course_no}` : 'Course 1'} · {clock(job.fired_at)}
      </p>
      {/* The note on the whole tab, above the dishes it is about. Paper does
          not update, so a note changed after this slip is on screens only. */}
      {job.order_note && <p className="slip__order-note">NOTE: {job.order_note}</p>}
      <hr className="slip__rule" />
      {dishes.map((item) => (
        <div key={item.id} className="slip__dish">
          <p className="slip__line">
            {Number(item.qty)} × {item.name}
          </p>
          {item.modifiers.length > 0 && (
            <p className="slip__sub">{item.modifiers.join(', ')}</p>
          )}
          {item.note && <p className="slip__sub slip__note">** {item.note} **</p>}
          {item.seat_no !== null && <p className="slip__sub">Seat {item.seat_no}</p>}
        </div>
      ))}
      {voids.map((item) => (
        <div key={item.id} className="slip__dish">
          <p className="slip__line slip__void">
            VOID {Number(item.qty)} × {item.name}
          </p>
          {item.seat_no !== null && <p className="slip__sub">Seat {item.seat_no}</p>}
        </div>
      ))}
    </div>
  );
}

function sameItems(a: PrintJob, b: PrintJob): boolean {
  const ids = new Set(a.items.map((item) => item.id));
  return b.items.every((item) => ids.has(item.id));
}

function clock(value: string | number): string {
  return new Date(value).toLocaleTimeString(displayLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  });
}
